import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const CalloutBox = makeShortcode("CalloutBox");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are two ways to implement feature flags in React:`}</p>
    <ol>
      <li parentName="ol">{`Using hooks.`}</li>
      <li parentName="ol">{`Using the `}<inlineCode parentName="li">{`<PostHogFeature>`}</inlineCode>{` component.`}</li>
    </ol>
    <h3 {...{
      "id": "method-1-using-hooks"
    }}>{`Method 1: Using hooks`}</h3>
    <p>{`PostHog provides several hooks to make it easy to use feature flags in your React app.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Hook`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagEnabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns a boolean indicating whether the feature flag is enabled. This sends a `}<inlineCode parentName="td">{`$feature_flag_called`}</inlineCode>{` event.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagVariantKey`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns the variant key of the feature flag. This sends a `}<inlineCode parentName="td">{`$feature_flag_called`}</inlineCode>{` event.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useActiveFeatureFlags`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns an array of active feature flags. This does `}<em parentName="td">{`not`}</em>{` send a `}<inlineCode parentName="td">{`$feature_flag_called`}</inlineCode>{` event.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagPayload`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns the payload of the feature flag. This does `}<em parentName="td">{`not`}</em>{` send a `}<inlineCode parentName="td">{`$feature_flag_called`}</inlineCode>{` event. Always use this with `}<inlineCode parentName="td">{`useFeatureFlagEnabled`}</inlineCode>{` or `}<inlineCode parentName="td">{`useFeatureFlagVariantKey`}</inlineCode>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "example-1-using-a-boolean-feature-flag"
    }}>{`Example 1: Using a boolean feature flag`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagEnabled } from '@posthog/react'

function App() {
  const showWelcomeMessage = useFeatureFlagEnabled('flag-key')
  const payload = useFeatureFlagPayload('flag-key')

  return (
    <div className="App">
      {
        showWelcomeMessage ? (
          <div>
            <h1>Welcome!</h1>
            <p>Thanks for trying out our feature flags.</p>
          </div>
        ) : (
          <div>
            <h2>No welcome message</h2>
            <p>Because the feature flag evaluated to false.</p>
          </div>
        )
      }
    </div>
  );
}

export default App;
`}</code></pre>
    <h4 {...{
      "id": "example-2-using-a-multivariate-feature-flag"
    }}>{`Example 2: Using a multivariate feature flag`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagVariantKey } from '@posthog/react'

function App() {
  const variantKey = useFeatureFlagVariantKey('show-welcome-message')
  let welcomeMessage = '' 
  if (variantKey === 'variant-a') {
    welcomeMessage = 'Welcome to the Alpha!'
  } else if (variantKey === 'variant-b') {
    welcomeMessage = 'Welcome to the Beta!'
  }  

  return (
    <div className="App">
      {
        welcomeMessage ? (
          <div>
            <h1>{welcomeMessage}</h1>
            <p>Thanks for trying out our feature flags.</p>
          </div>
        ) : (
          <div>
            <h2>No welcome message</h2>
            <p>Because the feature flag evaluated to false.</p>
          </div>
        )
      }
    </div>
  );
}

export default App;
`}</code></pre>
    <h4 {...{
      "id": "example-3-using-a-flag-payload"
    }}>{`Example 3: Using a flag payload`}</h4>
    <CalloutBox icon="IconWarning" title="Payload hook" type="fyi" mdxType="CalloutBox">
      <p>{`The `}<inlineCode parentName="p">{`useFeatureFlagPayload`}</inlineCode>{` hook does `}<em parentName="p">{`not`}</em>{` send a `}<a parentName="p" {...{
          "href": "https://posthog.com/docs/experiments/new-experimentation-engine#experiment-exposure"
        }}><inlineCode parentName="a">{`$feature_flag_called`}</inlineCode></a>{` event, which is required for the experiment to be tracked. To ensure the exposure event is sent, you should `}<strong parentName="p">{`always`}</strong>{` use the `}<inlineCode parentName="p">{`useFeatureFlagPayload`}</inlineCode>{` hook with either the `}<inlineCode parentName="p">{`useFeatureFlagEnabled`}</inlineCode>{` or `}<inlineCode parentName="p">{`useFeatureFlagVariantKey`}</inlineCode>{` hook.`}</p>
    </CalloutBox>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagPayload } from '@posthog/react'

function App() {
  const variant = useFeatureFlagEnabled('show-welcome-message')
  const payload = useFeatureFlagPayload('show-welcome-message')

    return (
                <>
                {
                    variant ? (
                        <div className="welcome-message">
                            <h2>{payload?.welcomeTitle}</h2>
                            <p>{payload?.welcomeMessage}</p>
                        </div>
                    ) : <div>
                        <h2>No custom welcome message</h2>
                        <p>Because the feature flag evaluated to false.</p>
                    </div>
                }
        </>
    )
}
`}</code></pre>
    <h3 {...{
      "id": "method-2-using-the-posthogfeature-component"
    }}>{`Method 2: Using the PostHogFeature component`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`PostHogFeature`}</inlineCode>{` component simplifies code by handling feature flag related logic. `}</p>
    <p>{`It also automatically captures metrics, like how many times a user interacts with this feature.`}</p>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` You still need the `}<a parentName="p" {...{
          "href": "/docs/libraries/react#installation"
        }}><inlineCode parentName="a">{`PostHogProvider`}</inlineCode></a>{` at the top level for this to work.`}</p>
    </blockquote>
    <p>{`Here is an example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { PostHogFeature } from '@posthog/react'

function App() {

    return (
        <PostHogFeature flag='show-welcome-message' match={true}>
            <div>
                <h1>Hello</h1>
                <p>Thanks for trying out our feature flags.</p>
            </div>
        </PostHogFeature>
    )
}
`}</code></pre>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`match`}</inlineCode>{` on the component can be either `}<inlineCode parentName="p">{`true`}</inlineCode>{`, or the variant key, to match on a specific variant.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If you also want to show a default message, you can pass these in the `}<inlineCode parentName="p">{`fallback`}</inlineCode>{` attribute.`}</p>
      </li>
    </ul>
    <p>{`If you wish to customise logic around when the component is considered visible, you can pass in `}<inlineCode parentName="p">{`visibilityObserverOptions`}</inlineCode>{` to the feature. These take the same options as the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API"
      }}>{`IntersectionObserver API`}</a>{`. By default, we use a threshold of 0.1. `}</p>
    <h4 {...{
      "id": "payloads"
    }}>{`Payloads`}</h4>
    <p>{`If your flag has a payload, you can pass a function to children whose first argument is the payload. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { PostHogFeature } from '@posthog/react'

function App() {

    return (
        <PostHogFeature flag='show-welcome-message' match={true}>
           {(payload) => {
                return (
                    <div>
                        <h1>{payload.welcomeMessage}</h1>
                        <p>Thanks for trying out our feature flags.</p>
                    </div>
                )
           }}
        </PostHogFeature>
    )
}
`}</code></pre>
    <h3 {...{
      "id": "request-timeout"
    }}>{`Request timeout`}</h3>
    <p>{`You can configure the `}<inlineCode parentName="p">{`feature_flag_request_timeout_ms`}</inlineCode>{` parameter when initializing your PostHog client to set a flag request timeout. This helps prevent your code from being blocked in the case when PostHog's servers are too slow to respond. By default, this is set at 3 seconds.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`posthog.init('<ph_project_api_key>', { 
  api_host: '<ph_client_api_host>',
  defaults: '<ph_posthog_js_defaults>'
  feature_flag_request_timeout_ms: 3000 // Time in milliseconds. Default is 3000 (3 seconds).
}
)
`}</code></pre>
    <h3 {...{
      "id": "error-handling"
    }}>{`Error handling`}</h3>
    <p>{`When using the PostHog SDK, it's important to handle potential errors that may occur during feature flag operations. Here's an example of how to wrap PostHog SDK methods in an error handler:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function handleFeatureFlag(client, flagKey, distinctId) {
    try {
        const isEnabled = client.isFeatureEnabled(flagKey, distinctId);
        console.log(\`Feature flag '\${flagKey}' for user '\${distinctId}' is \${isEnabled ? 'enabled' : 'disabled'}\`);
        return isEnabled;
    } catch (error) {
        console.error(\`Error fetching feature flag '\${flagKey}': \${error.message}\`);
        // Optionally, you can return a default value or throw the error
        // return false; // Default to disabled
        throw error;
    }
}

// Usage example
try {
    const flagEnabled = handleFeatureFlag(client, 'new-feature', 'user-123');
    if (flagEnabled) {  
        // Implement new feature logic
    } else {
        // Implement old feature logic
    }
} catch (error) {
    // Handle the error at a higher level
    console.error('Feature flag check failed, using default behavior');
    // Implement fallback logic
} 
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      